/**
 * Centralized agent naming constants for frontend.
 * Single source of truth - import from here instead of hardcoding.
 *
 * Usage:
 *   import { AgentId, normalizeAgentName, AGENT_ALIASES } from '@/constants/agentNames';
 *
 *   // Use canonical identifiers
 *   const agent = AgentId.MECHANICAL; // "mechanical_engineer"
 *
 *   // Normalize any name variant
 *   const canonical = normalizeAgentName("max"); // "mechanical_engineer"
 */

export const AgentId = {
  PLANT_SUPPORT: 'plant_support_engineer',
  CHEMICAL: 'chemical_engineer',
  MECHANICAL: 'mechanical_engineer',
  ELECTRICAL: 'electrical_engineer',
  DOCUMENT_RAG: 'document_rag_agent',
  PID_GRAPH: 'pid_graph_agent',
  SQL: 'sql_agent',
  KNOWLEDGE_GRAPH: 'knowledge_graph_rag_agent',
  PROCESS_OPERATIONS: 'process_operations_expert',
  HYBRID_DOCUMENT_KG_RAG: 'hybrid_document_kg_rag_agent',
} as const;

export type AgentIdType = (typeof AgentId)[keyof typeof AgentId];

export interface AgentAlias {
  canonical: AgentIdType;
  legacyShort: string;
  mentionFull: string;
  mentionShort: string;
  displayName: string;
  color: string;
}

/**
 * Master mapping of all agent aliases with their properties.
 */
export const AGENT_ALIASES: Record<string, AgentAlias> = {
  [AgentId.PLANT_SUPPORT]: {
    canonical: AgentId.PLANT_SUPPORT,
    legacyShort: 'pam',
    mentionFull: '@ai-plant-support-engineer',
    mentionShort: '@pam',
    displayName: 'AI Plant Support Engineer',
    color: 'amber',
  },
  [AgentId.CHEMICAL]: {
    canonical: AgentId.CHEMICAL,
    legacyShort: 'aice',
    mentionFull: '@ai-chemical-engineer',
    mentionShort: '@aice',
    displayName: 'AI Chemical Engineer',
    color: 'blue',
  },
  [AgentId.MECHANICAL]: {
    canonical: AgentId.MECHANICAL,
    legacyShort: 'max',
    mentionFull: '@ai-mechanical-engineer',
    mentionShort: '@max',
    displayName: 'AI Mechanical Engineer',
    color: 'green',
  },
  [AgentId.ELECTRICAL]: {
    canonical: AgentId.ELECTRICAL,
    legacyShort: 'ella',
    mentionFull: '@ai-electrical-engineer',
    mentionShort: '@ella',
    displayName: 'AI Electrical Engineer',
    color: 'purple',
  },
};

/**
 * Legacy to canonical name mapping.
 * Includes old short names, transfer functions, and canonical names.
 */
export const LEGACY_TO_CANONICAL: Record<string, AgentIdType> = {
  // Old short names
  pam: AgentId.PLANT_SUPPORT,
  aice: AgentId.CHEMICAL,
  max: AgentId.MECHANICAL,
  ella: AgentId.ELECTRICAL,
  // Old agent names
  general_purpose_agent: AgentId.PLANT_SUPPORT,
  // New canonical names (for completeness)
  plant_support_engineer: AgentId.PLANT_SUPPORT,
  chemical_engineer: AgentId.CHEMICAL,
  mechanical_engineer: AgentId.MECHANICAL,
  electrical_engineer: AgentId.ELECTRICAL,
  document_rag_agent: AgentId.DOCUMENT_RAG,
  pid_graph_agent: AgentId.PID_GRAPH,
  sql_agent: AgentId.SQL,
  knowledge_graph_rag_agent: AgentId.KNOWLEDGE_GRAPH,
  process_operations_expert: AgentId.PROCESS_OPERATIONS,
  hybrid_document_kg_rag_agent: AgentId.HYBRID_DOCUMENT_KG_RAG,
};

/**
 * Normalize any agent name variant to canonical form.
 *
 * @param name - Any agent name (legacy or new)
 * @returns Canonical agent name
 *
 * @example
 * normalizeAgentName("max") // "mechanical_engineer"
 * normalizeAgentName("pam") // "plant_support_engineer"
 */
export function normalizeAgentName(name: string): AgentIdType | string {
  const normalized = name.toLowerCase().trim();
  return LEGACY_TO_CANONICAL[normalized] ?? normalized;
}

/**
 * Check if a string is a valid agent identifier (legacy or canonical).
 */
export function isValidAgent(name: string): boolean {
  const normalized = name.toLowerCase().trim();
  return normalized in LEGACY_TO_CANONICAL;
}

/**
 * Get display name for an agent.
 *
 * @param agentId - Agent identifier (can be legacy or canonical)
 * @returns Human-readable display name
 */
export function getDisplayName(agentId: string): string {
  const canonical = normalizeAgentName(agentId);
  const alias = AGENT_ALIASES[canonical];
  if (alias) {
    return alias.displayName;
  }
  // Fallback: format the name nicely
  return canonical.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Resolve which agent persona a chat response shows (avatar + label).
 *
 * The unified backend stamps every board-chat response with the generic
 * `plant_support_engineer`, so an explicit user @mention must win over it —
 * otherwise calling `@ai-chemical-engineer` shows the plant-support avatar.
 *
 * Precedence: user @mention -> backend agent_used -> caller fallback -> default.
 */
export function resolveResponseDisplayAgent(opts: {
  userRequestedAgent?: string | null;
  backendAgentUsed?: string | null;
  fallbackAgent?: string | null;
}): string {
  return (
    opts.userRequestedAgent ||
    opts.backendAgentUsed ||
    opts.fallbackAgent ||
    AgentId.PLANT_SUPPORT
  );
}

/**
 * Get agent alias info by legacy short name.
 */
export function getAgentByLegacyName(legacyName: string): AgentAlias | undefined {
  const lowerName = legacyName.toLowerCase();
  return Object.values(AGENT_ALIASES).find(
    (alias) => alias.legacyShort === lowerName
  );
}

/**
 * Generate mention pattern regex from constants.
 * Matches both full and short mention patterns.
 */
export function getMentionPattern(): RegExp {
  const patterns = Object.values(AGENT_ALIASES)
    .flatMap((alias) => [alias.mentionFull, alias.mentionShort])
    .map((p) => p.replace('@', '').replace(/-/g, '\\-'));
  return new RegExp(`(@(?:${patterns.join('|')}))`, 'gi');
}

/**
 * Get all valid mention patterns as strings.
 */
export function getAllMentionPatterns(): string[] {
  return Object.values(AGENT_ALIASES).flatMap((alias) => [
    alias.mentionFull,
    alias.mentionShort,
  ]);
}

/**
 * Get AgentAlias from a mention string.
 */
export function getAgentByMention(mention: string): AgentAlias | undefined {
  const mentionLower = mention.toLowerCase().trim();
  return Object.values(AGENT_ALIASES).find(
    (alias) =>
      alias.mentionFull.toLowerCase() === mentionLower ||
      alias.mentionShort.toLowerCase() === mentionLower
  );
}

/**
 * Extract the agent a user @mentioned in a message as a canonical AgentId
 * (first mention wins), or null if none. The backend persists only the
 * generic plant_support_engineer on replies, so the user's @mention is the
 * real signal for which persona to show on the answer (avatar + label).
 */
export function getMentionedAgent(text: string): AgentIdType | null {
  const match = getMentionPattern().exec(text);
  if (!match) return null;
  return getAgentByMention(match[1])?.canonical ?? null;
}

/**
 * Avatar background colors keyed by agent-persona store ID. Shared by the
 * chat agent grid (EmptyChatStateV2) and the input-footer agent trigger
 * (BoardChatPanelV2) so the same agent never renders two different colors.
 */
export const AGENT_STORE_BG_COLORS: Record<string, string> = {
  'plant-support': '#FEF08A',
  'chemical-agent': '#BFDBFE',
  'mechanical-agent': '#BBF7D0',
  'electrical-agent': '#E9D5FF',
  'maint-agent': '#DDD6FE',
  'epc-agent': '#FED7AA',
}

export const DEFAULT_AGENT_BG_COLOR = '#E5E7EB'

/**
 * Mapping from canonical backend agent ID to the agent-persona store ID
 * used by the frontend store (see agentPersonasSlice).
 */
export const BACKEND_TO_STORE_AGENT_ID: Partial<Record<AgentIdType, string>> = {
  [AgentId.PLANT_SUPPORT]: 'plant-support',
  [AgentId.CHEMICAL]: 'chemical-agent',
  [AgentId.MECHANICAL]: 'mechanical-agent',
  [AgentId.ELECTRICAL]: 'electrical-agent',
}

/**
 * Agent-persona avatar image (served from /public/images) keyed by canonical
 * agent id. Mirrors the chat avatar config so workflow executor avatars and the
 * chat agents pull the same persona faces.
 */
export const AGENT_AVATAR_IMAGES: Partial<Record<AgentIdType, string>> = {
  [AgentId.PLANT_SUPPORT]: '/images/plant-support-agent.png',
  [AgentId.CHEMICAL]: '/images/chemical-agent.png',
  [AgentId.MECHANICAL]: '/images/mechanical-agent.png',
  [AgentId.ELECTRICAL]: '/images/elec-agent.png',
}

/**
 * Resolve an agent name/persona — a canonical id, legacy short name, display
 * name ("AI Chemical Engineer"), or a title containing the discipline word — to
 * its avatar image. Returns undefined when the name isn't a recognized agent
 * persona, so the caller falls back to initials.
 */
export function getAgentAvatarImage(name: string): string | undefined {
  const raw = name.toLowerCase().trim()
  const direct = AGENT_AVATAR_IMAGES[normalizeAgentName(raw) as AgentIdType]
  if (direct) return direct
  const byDisplay = Object.values(AGENT_ALIASES).find((a) => a.displayName.toLowerCase() === raw)
  // Only return on a display-name hit if it actually maps to an image; otherwise
  // fall through to the keyword match rather than short-circuiting to undefined.
  if (byDisplay && AGENT_AVATAR_IMAGES[byDisplay.canonical]) return AGENT_AVATAR_IMAGES[byDisplay.canonical]
  if (raw.includes('chemical')) return AGENT_AVATAR_IMAGES[AgentId.CHEMICAL]
  if (raw.includes('mechanical')) return AGENT_AVATAR_IMAGES[AgentId.MECHANICAL]
  if (raw.includes('electrical')) return AGENT_AVATAR_IMAGES[AgentId.ELECTRICAL]
  if (raw.includes('plant support') || raw.includes('plant_support')) {
    return AGENT_AVATAR_IMAGES[AgentId.PLANT_SUPPORT]
  }
  return undefined
}

export interface UnavailableMention {
  /** The raw mention text the user typed (e.g. "@ai-electrical-engineer" or "@ella"). */
  mention: string
  /** Canonical backend agent ID the mention maps to. */
  canonical: AgentIdType
  /** Full-form mentions (e.g. "@ai-chemical-engineer") for agents that are currently enabled. */
  availableMentions: string[]
}

/**
 * Scan `message` for the first @-mention that refers to an agent not present
 * in `enabledStoreIds`. Returns null if every mention is available.
 *
 * `enabledStoreIds` is the list of store agent IDs (e.g. "chemical-agent")
 * that are provisioned and enabled for the current workspace.
 */
export function findUnavailableMention(
  message: string,
  enabledStoreIds: readonly string[],
): UnavailableMention | null {
  const enabled = new Set(enabledStoreIds)
  const pattern = getMentionPattern()
  const isCanonicalEnabled = (canonical: AgentIdType): boolean => {
    const storeId = BACKEND_TO_STORE_AGENT_ID[canonical]
    return storeId ? enabled.has(storeId) : true
  }

  let match: RegExpExecArray | null
  while ((match = pattern.exec(message)) !== null) {
    const mentionText = match[1]
    const alias = getAgentByMention(mentionText)
    if (!alias) continue
    if (isCanonicalEnabled(alias.canonical)) continue

    const availableMentions = Object.values(AGENT_ALIASES)
      .filter((a) => isCanonicalEnabled(a.canonical))
      .map((a) => a.mentionFull)

    return {
      mention: mentionText,
      canonical: alias.canonical,
      availableMentions,
    }
  }
  return null
}

/**
 * Backend agents that should not be visible to users in the chat interface.
 * These are internal routing agents used by the system.
 */
export const BACKEND_AGENT_IDS = [
  AgentId.PID_GRAPH,
  AgentId.SQL,
  AgentId.DOCUMENT_RAG,
  AgentId.KNOWLEDGE_GRAPH,
  AgentId.PROCESS_OPERATIONS,
  AgentId.HYBRID_DOCUMENT_KG_RAG,
] as const;

/**
 * Generate regex pattern to match backend agent mentions.
 * Used to strip these mentions from user-visible text.
 *
 * @returns RegExp that matches @backend_agent patterns
 */
export function getBackendAgentPattern(): RegExp {
  // Escape special regex characters in agent IDs for safety
  const escapedAgentIds = BACKEND_AGENT_IDS.map(id =>
    id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  ).join('|');
  return new RegExp(`@(${escapedAgentIds})\\b`, 'gi');
}
